import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, X, Search, Eye, Edit2, Trash2, Send, FileText, Mail, Download } from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { invoiceAPI, candidatesAPI, clientsAPI } from "../../services/api";
import { toast } from "react-toastify";
import { getWhatsAppNumber } from '../Templates/templateUtils';
import EmailTemplatePanel from '../Templates/EmailTemplatePanel';
import WhatsAppTemplatePanel from '../Templates/WhatsAppTemplatePanel';
import Delete from "../common/Delete";
import CreateInvoiceModal from "./CreateInvoiceModal";
import EditInvoiceModal from "./EditInvoiceModal";
import InvoicePreview from "./InvoicePreview";

const Invoices = () => {
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [clients, setClients] = useState([]);
  const [emailPanelInvoice, setEmailPanelInvoice] = useState(null);
  const [whatsappPanelInvoice, setWhatsappPanelInvoice] = useState(null);

  const [filters, setFilters] = useState({
    clientId: null,
    candidateId: null,
    placementType: null,
    feeModel: null,
    startDate: '',
    endDate: ''
  });

  const placementTypeOptions = [
    { value: 'Permanent', label: 'Permanent' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Contract-to-Hire', label: 'Contract-to-Hire' },
    { value: 'Temporary', label: 'Temporary' }
  ];

  const feeModelOptions = [
    { value: 'Percentage', label: 'Percentage' },
    { value: 'Fixed', label: 'Fixed Amount' },
    { value: 'Hybrid', label: 'Hybrid' }
  ];

  // Fetch all invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const userProfileStr = localStorage.getItem('userProfile');

let organizationId = null;

if (userProfileStr) {
  const userProfile = JSON.parse(userProfileStr);
  organizationId = userProfile.organizationId || userProfile.id || localStorage.getItem('organizationId');
}

      const response = await invoiceAPI.getAllInvoices(organizationId);
      let invoicesData = Array.isArray(response.data) ? response.data : response.data.content || [];
      setInvoices(invoicesData);
      setFilteredInvoices(invoicesData);
      console.log(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  // Fetch candidates for dropdown
  const fetchCandidates = async () => {
    try {
      const response = await candidatesAPI.getAllApplicationsByOrg();
      let candidatesData = Array.isArray(response.data) ? response.data : response.data.content || [];
      setCandidates(candidatesData);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAllClients();
      let clientsData = Array.isArray(response.data) ? response.data : response.data.content || [];
      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchCandidates();
    fetchClients();
  }, []);

  // Auto-open create modal when navigated from dashboard onboarding
  useEffect(() => {
    if (location.state?.openCreate) {
      setShowCreateModal(true);
    }
  }, [location.state]);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...invoices];

    // Filter by client
    if (filters.clientId) {
      filtered = filtered.filter(inv => inv.clientId === filters.clientId.value);
    }

    // Filter by candidate
    if (filters.candidateId) {
      filtered = filtered.filter(inv => inv.candidateId === filters.candidateId.value);
    }

    // Filter by placement type
    if (filters.placementType) {
      filtered = filtered.filter(inv => inv.placementType === filters.placementType.value);
    }

    // Filter by fee model
    if (filters.feeModel) {
      filtered = filtered.filter(inv => inv.feeModel === filters.feeModel.value);
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(inv => {
        const joiningDate = new Date(inv.joiningDate);
        return joiningDate >= new Date(filters.startDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(inv => {
        const joiningDate = new Date(inv.joiningDate);
        return joiningDate <= new Date(filters.endDate);
      });
    }

    // Search query
    if (searchQuery) {
      filtered = filtered.filter(inv =>
        inv.candidateName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.poNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.designation?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
  }, [filters, invoices, searchQuery]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      clientId: null,
      candidateId: null,
      placementType: null,
      feeModel: null,
      startDate: '',
      endDate: ''
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredInvoices.map(inv => inv.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    try {
      for (const id of selectedRows) {
        await invoiceAPI.deleteInvoice(id);
      }
      toast.success('Invoice(s) deleted successfully');
      setSelectedRows([]);
      setShowDeleteModal(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoices:', error);
      toast.error('Failed to delete invoice(s)');
    }
  };

  const handleEdit = (invoice) => {
    setSelectedInvoiceForEdit(invoice);
    setShowEditModal(true);
  };

  const handlePreview = (invoice) => {
    setSelectedInvoiceForPreview(invoice);
    setShowPreviewModal(true);
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await invoiceAPI.getPlacementOrderPdf(invoice.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleResendEmail = async (invoice) => {
    try {
      await invoiceAPI.resendInvoiceEmail(invoice.id);
      toast.success('Invoice email sent successfully');
    } catch (error) {
      console.error('Error sending invoice email:', error);
      const errMsg = error.response?.data?.message || '';
      if (errMsg.includes('No email address')) {
        toast.error('Client email is missing. Please edit the invoice and add the client email before sending.');
      } else {
        toast.error(errMsg || 'Failed to send invoice email');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoices & Placement Orders</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus size={16} />
              Create Invoice
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-md flex items-center justify-between">
            <span className="text-sm text-blue-900 font-medium">
              {selectedRows.length} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-3 py-1 text-sm text-red-700 hover:bg-red-100 rounded"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedRows([])}
                className="p-1 text-blue-700 hover:bg-blue-100 rounded"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Client Filter */}
            <div className="w-48">
              <select
                value={filters.clientId?.value || ''}
                onChange={(e) => {
                  const selected = clients.find(c => c.id === e.target.value);
                  handleFilterChange('clientId', selected ? { value: selected.id, label: selected.clientName } : null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Clients</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.clientName}</option>
                ))}
              </select>
            </div>

            {/* Candidate Filter */}
            <div className="w-48">
              <select
                value={filters.candidateId?.value || ''}
                onChange={(e) => {
                  const selected = candidates.find(c => c.id === e.target.value);
                  handleFilterChange('candidateId', selected ? { value: selected.id, label: `${selected.firstName} ${selected.lastName}` } : null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Candidates</option>
                {candidates.map(candidate => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.firstName} {candidate.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Placement Type Filter */}
            <div className="w-48">
              <select
                value={filters.placementType?.value || ''}
                onChange={(e) => {
                  const selected = placementTypeOptions.find(pt => pt.value === e.target.value);
                  handleFilterChange('placementType', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Types</option>
                {placementTypeOptions.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Fee Model Filter */}
            <div className="w-48">
              <select
                value={filters.feeModel?.value || ''}
                onChange={(e) => {
                  const selected = feeModelOptions.find(fm => fm.value === e.target.value);
                  handleFilterChange('feeModel', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">All Fee Models</option>
                {feeModelOptions.map(model => (
                  <option key={model.value} value={model.value}>{model.label}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <input
                type="date"
                placeholder="Start Date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-36 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <span className="text-gray-500">-</span>
              <input
                type="date"
                placeholder="End Date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-36 px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            {/* Clear Filters */}
            {(filters.clientId || filters.candidateId || filters.placementType || filters.feeModel || filters.startDate || filters.endDate) && (
              <button 
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-md"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center justify-between p-4 border-b gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search by candidate, client, PO number, or designation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FileText size={48} className="mb-4 text-gray-300" />
            <p className="text-lg font-medium">No invoices found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create a new invoice</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredInvoices.length && filteredInvoices.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Designation
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Placement Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CTC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fee
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joining Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className={`group hover:bg-gray-50 ${
                    selectedRows.includes(invoice.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(invoice.id)}
                      onChange={() => handleRowSelect(invoice.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {invoice.candidateName || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span>{invoice.clientName || '-'}</span>
                      {(() => {
                        const client = clients.find(c => c.id === invoice.clientId);
                        const clientEmail = client?.primaryContactEmail || client?.email;
                        const validWA = getWhatsAppNumber({ phone: client?.primaryContactPhone });
                        return (
                          <>
                            {clientEmail && (
                              <button onClick={e => { e.stopPropagation(); setEmailPanelInvoice(invoice); }}
                                title="Send email from template"
                                className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded text-blue-500 hover:text-blue-600 hover:bg-blue-50 cursor-pointer">
                                <Mail size={13} />
                              </button>
                            )}
                            {validWA ? (
                              <button onClick={e => { e.stopPropagation(); setWhatsappPanelInvoice(invoice); }}
                                title="Send WhatsApp"
                                className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded text-[#25D366] hover:bg-green-50 cursor-pointer">
                                <IoLogoWhatsapp size={13} />
                              </button>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {invoice.designation || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {invoice.placementType || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatCurrency(invoice.ctc)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatCurrency(invoice.calculatedFee)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatDate(invoice.joiningDate)}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <button
                      onClick={() => { setSelectedInvoiceForPreview(invoice); setShowPreviewModal(true); }}
                      className="text-[#1C4E6D] font-medium hover:underline cursor-pointer">
                      {invoice.invoiceNumber || invoice.poNumber || '-'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      invoice.invoiceStatus === 'PAID' ? 'bg-green-100 text-green-800' :
                      invoice.invoiceStatus === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                      invoice.invoiceStatus === 'SENT' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {invoice.invoiceStatus || 'Draft'}
                    </span>
                    {invoice.invoiceSource === 'AUTO_DRAFT' && (
                      <span className="ml-1 px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                        AUTO
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePreview(invoice)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded cursor-pointer"
                        title="Preview invoice"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded cursor-pointer"
                        title="Edit invoice"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleResendEmail(invoice)}
                        className="p-1 text-green-600 hover:bg-green-100 rounded cursor-pointer"
                        title="Resend email"
                      >
                        <Send size={16} />
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(invoice)}
                        className="p-1 text-purple-600 hover:bg-purple-100 rounded cursor-pointer"
                        title="Download PDF"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRows([invoice.id]);
                          setShowDeleteModal(true);
                        }}
                        className="p-1 text-red-600 hover:bg-red-100 rounded cursor-pointer"
                        title="Delete invoice"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateInvoiceModal
          candidates={candidates}
          clients={clients}
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            fetchInvoices();
          }}
          onSuccess={() => fetchInvoices()}
        />
      )}

      {showEditModal && selectedInvoiceForEdit && (
        <EditInvoiceModal
          invoice={selectedInvoiceForEdit}
          candidates={candidates}
          clients={clients}
          onClose={() => {
            setShowEditModal(false);
            setSelectedInvoiceForEdit(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedInvoiceForEdit(null);
            fetchInvoices();
          }}
        />
      )}

      {showPreviewModal && selectedInvoiceForPreview && (
        <InvoicePreview
          invoice={selectedInvoiceForPreview}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedInvoiceForPreview(null);
          }}
        />
      )}

      {showDeleteModal && (
        <Delete
          count={selectedRows.length}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
      )}

      <EmailTemplatePanel
        isOpen={Boolean(emailPanelInvoice)}
        onClose={() => setEmailPanelInvoice(null)}
        module="INVOICE"
        context={emailPanelInvoice ? {
          clientName: emailPanelInvoice.clientName || '',
          candidateName: emailPanelInvoice.candidateName || '',
          jobRole: emailPanelInvoice.designation || '',
          invoiceAmount: emailPanelInvoice.calculatedFee ? String(emailPanelInvoice.calculatedFee) : '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={emailPanelInvoice ? {
          email: clients.find(c => c.id === emailPanelInvoice.clientId)?.primaryContactEmail || clients.find(c => c.id === emailPanelInvoice.clientId)?.email,
          name: emailPanelInvoice.clientName || '',
        } : null}
      />
      <WhatsAppTemplatePanel
        isOpen={Boolean(whatsappPanelInvoice)}
        onClose={() => setWhatsappPanelInvoice(null)}
        module="INVOICE"
        context={whatsappPanelInvoice ? {
          clientName: whatsappPanelInvoice.clientName || '',
          candidateName: whatsappPanelInvoice.candidateName || '',
          jobRole: whatsappPanelInvoice.designation || '',
          invoiceAmount: whatsappPanelInvoice.calculatedFee ? String(whatsappPanelInvoice.calculatedFee) : '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={whatsappPanelInvoice ? {
          phone: getWhatsAppNumber({ phone: clients.find(c => c.id === whatsappPanelInvoice.clientId)?.primaryContactPhone }),
          phoneSource: 'primaryContactPhone',
          name: whatsappPanelInvoice.clientName || '',
        } : null}
      />
    </div>
  );
};

export default Invoices;